import React from 'react'
import {connect} from 'react-redux'
import {Link} from 'react-router'
import styles from './CollectionMap.scss'
import {ROUTE_COLORS} from 'features/embed/RegionalEmbed/RegionalEmbedlet'
import {collectionItemSelectors} from 'data/resources/collectionItems'
import {routeSetOverviewSelectors} from 'data/resources/routeSetOverviews'
import {routeSelectors} from 'data/resources/routes'
import {tripSelectors} from 'data/resources/trips'
import {loadGeoJSON} from 'lib/api/embeds/regional'
import preloadImg from 'lib/preloadImg'
import {northernmostPoint} from 'lib/geoUtils'
import {clampStringWords, capitalize} from 'lib/utils'
import {urls} from 'lib/urls'
import {photoMedium} from 'lib/urlHelpers'
import {Loadable} from 'common/Loadable'
import {Distance, Elevation} from 'common/units'
import {MapV2} from 'common/MapV2'
import {TrackLine} from 'common/MapV2/TrackLine'
import {FullscreenButton} from 'common/MapV2/controls/FullscreenButton'
import {Popup} from 'common/Map/Popup'
import {Topo} from 'common/styleguide/Topo'
import {Viewport} from 'data/records'
import mobileMap from './mobileMap.png'
import {FormattedMessage as T} from 'react-intl'
import {Map} from 'immutable'
import _ from 'lodash'

export class _CollectionMap extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      geoJSONFeatureMap: Map(),
      geoJSONParsed: false,
      selectedFeature: null,
      hoveredFeatureId: null,
      showMobleMap: false
    }
  }

  componentDidMount = () => {
    window.getBounds = this.getBounds
    this.preloadImages()
  }

  preloadImages = () => {
    const {routeSetOverview, findTrip, findRoute} = this.props

    if (!routeSetOverview) return

    routeSetOverview.routeIds.forEach(id => {
      const route = findRoute(id)
      if (!route) {
        console.warn('could not find route metadata for route ' + id)
        return
      }
      const pid = route.highlightedPhotoId
      if (!pid) { return }
      preloadImg(photoMedium(pid))
      console.log('preloading image ' + pid)
    })

    routeSetOverview.tripIds.forEach(id => {
      const trip = findTrip(id)
      if (!trip) {
        console.warn('could not find trip metadata for trip ' + id)
        return
      }
      const pid = trip.highlightedPhotoId
      if (!pid) { return }
      preloadImg(photoMedium(pid))
      console.log('preloading image ' + pid)
    })
  }

  parseIntoViewport = (bbStr) => {
    if (!bbStr) {
      return undefined
    }

    const boundsArr = bbStr.split(',').map(parseFloat)
    return Viewport({bounds: [
      {
        lng: boundsArr[0],
        lat: boundsArr[1]
      },
      {
        lng: boundsArr[2],
        lat: boundsArr[3]
      }
    ]})
  }

  onMouseEnterTrackLine = (featureId) => {
    const m = this._map._mapEngine._map
    m.queryRenderedFeatures().forEach(f => {
      f.layer.id.slice(0, f.layer.id.length - 1) === featureId.slice(0, featureId.length - 1)
        ? m.setPaintProperty(featureId, 'line-opacity', 1)
        : m.setPaintProperty(f.layer.id, 'line-opacity', 0.5)
    })
  }

  onMouseLeaveTrackLine = () => {
    const m = this._map._mapEngine._map
    m.queryRenderedFeatures().forEach(f => {
      m.setPaintProperty(f.layer.id, 'line-opacity', 1)
    })
  }

  // geoJSON feature geometry is an array of arrays, <TrackLine /> wants {lat, lng}
  trackPointsFromGeoJSON = _.memoize(features => {
    let map = Map()
    let featureMap = Map({trip: [], route: []})
    features.forEach(f => {
      map = map.setIn([f.properties.type, f.properties.id], f.geometry.coordinates.map(c => ({lat: c[1], lng: c[0]})))
      featureMap = featureMap.set(f.properties.type, featureMap.get(f.properties.type).concat(f.properties.id))
    })
    this.setState({geoJSONFeatureMap: featureMap, geoJSONParsed: true})
    return map
  })

  // verify that a feature is both associated with the collection in redux, and is
  // included in routeSetOverview assets
  isAnAsset = (f) => {
    const {collectionId, collectionItemByItem, routeSetOverview} = this.props
    const {id, type} = f.properties
    const collectionItem = collectionItemByItem(id, type)

    const idsProperty = ({trip: 'tripIds', route: 'routeIds'})[type]
    return !!routeSetOverview[idsProperty].includes(id) && (collectionItem && collectionItem.collectionId === collectionId)
  }

  renderGeoJSONFeatures = () => {
    const {loadableResources: {geoJSON}} = this.props
    const tpMap = this.trackPointsFromGeoJSON(geoJSON.features)

    const geoJSONFeatures = geoJSON.features.filter(f => this.isAnAsset(f))
    return geoJSONFeatures.map((f, idx) =>
      <TrackLine
        key={`${f.properties.type}-${f.properties.id}`}
        color={ROUTE_COLORS[idx % ROUTE_COLORS.length]}
        showStartMarker={false}
        showEndMarker={false}
        onMouseEnter={this.onMouseEnterTrackLine}
        onMouseLeave={this.onMouseLeaveTrackLine}
        onClick={() => this.setState({selectedFeature: {type: f.properties.type, id: f.properties.id}})}
        trackPoints={tpMap.getIn([f.properties.type, f.properties.id])} />
    )
  }

  renderNonGeoJSONFeatures = () => {
    const {collectionItems, findTrip, findRoute} = this.props
    const {geoJSONFeatureMap, geoJSONParsed} = this.state
    if (!geoJSONParsed) {
      return
    }

    const itemsNotInGeoJSON = collectionItems.filter(i => geoJSONFeatureMap.get(i.itemType).indexOf(i.itemId) === -1)

    return itemsNotInGeoJSON.valueSeq().map((i, idx) => {
      const troute = i.itemType === 'route'
        ? findRoute(i.itemId)
        : findTrip(i.itemId)

      return <TrackLine
        key={`${i.itemType}-${i.itemId}`}
        color={ROUTE_COLORS[idx % ROUTE_COLORS.length - 1]}
        showStartMarker={false}
        showEndMarker={false}
        onMouseEnter={this.onMouseEnterTrackLine}
        onMouseLeave={this.onMouseLeaveTrackLine}
        onClick={() => this.setState({selectedFeature: {type: i.itemType, id: i.itemId}})}
        trackPoints={troute.trackPoints} />
    })
  }

  renderSelectedFeature = () => {
    const {geoJSONFeatureMap, selectedFeature} = this.state
    if (!selectedFeature) { return null }
    const {findTrip, findRoute, loadableResources: {geoJSON}} = this.props
    let troute, url
    if (selectedFeature.type === 'route') {
      troute = findRoute(selectedFeature.id)
      url = urls.routes.show(troute.id, troute.privacyCode)
    } else {
      troute = findTrip(selectedFeature.id)
      url = urls.trips.show(troute.id, troute.privacyCode)
    }

    // depending on what feature is selected, we need to get trackPoints from geoJSON or redux
    const position = geoJSONFeatureMap.get(selectedFeature.type).indexOf(selectedFeature.id) >= 0
      ? northernmostPoint(this.trackPointsFromGeoJSON(geoJSON.features).getIn([selectedFeature.type, selectedFeature.id]))
      : northernmostPoint(troute.trackPoints)

    return <Popup
      autoPan
      noPadding
      closeButton
      onRequestClose={() => this.setState({selectedFeature: null})}
      footer={<Link to={url} className={styles.footerLink}>View {capitalize(selectedFeature.type)}</Link>}
      position={position}>
      <div className={styles.wrap}>
        {troute.highlightedPhotoId > 0 && <div className={styles.photoWrap}>
          <Link to={url}>
            <img src={photoMedium(troute.highlightedPhotoId)} />
          </Link>
        </div>}
        <div className={styles.rest}>
          <h3><Link to={url}>{troute.name}</Link></h3>
          <p className={styles.small}>
            <span className={styles.metric}>
              <Distance short value={troute.distance} />
            </span>
            <span className={styles.metric}>
            +<Elevation short value={troute.elevationGain} />
            </span>
          </p>
          <p>{clampStringWords(troute.description || '', 140)}</p>
        </div>
      </div>
    </Popup>
  }

  onMapClick = ({e, feature}) => {
    !feature && this.setState({selectedFeature: null})
  }

  setShowMobileMap = (bool) => this.setState({showMobleMap: bool})

  render () {
    const {collapse, routeSetOverview} = this.props
    const {showMobleMap} = this.state
    const vp = this.parseIntoViewport(routeSetOverview.get('embedSettings')['bounding_box'])

    if (collapse) {
      if (showMobleMap) {
        return <Topo dark className={styles.mobileMap}>
          <MapV2
            ref={r => { this._map = r }}
            hideOverlayPicker
            onMapClick={this.onMapClick}
            initialViewport={vp}
            controls={<FullscreenButton isFullscreen={showMobleMap} onClick={() => this.setShowMobileMap(false)} />}>
            {this.renderGeoJSONFeatures()}
            {this.renderNonGeoJSONFeatures()}
            {this.renderSelectedFeature()}
          </MapV2>
        </Topo>
      } else {
        return <div className={styles.mobileMapPlaceholder}>
          <img src={mobileMap} onClick={() => this.setShowMobileMap(true)} />
        </div>
      }
    }

    return (
      <MapV2
        scrollWheelZoom={false}
        ref={r => { this._map = r }}
        hideOverlayPicker
        onMapClick={this.onMapClick}
        initialViewport={vp}>
        {this.renderGeoJSONFeatures()}
        {this.renderNonGeoJSONFeatures()}
        {this.renderSelectedFeature()}
      </MapV2>
    )
  }
}

const loadInitialResources = (props) => {
  const {routeSetOverview, updatedAt, loadGeoJSON} = props

  return loadGeoJSON(routeSetOverview.id, updatedAt).then(r => ({geoJSON: r.action.payload}))
}

const LoadableCollectionMap = (props) => {
  return <Loadable
    {...props}
    component={_CollectionMap}
    loadingText={<T id='CollectionMap.loadableText' defaultMessage='Loading your map' />}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  return {
    routeSetOverview: routeSetOverviewSelectors.byCollectionId(state, ownProps.collectionId),
    findRoute: (id) => routeSelectors.find(state, id),
    findTrip: (id) => tripSelectors.find(state, id),
    collectionItemByItem: (id, type) => collectionItemSelectors.byItem(state, id, type),
    collectionItems: collectionItemSelectors.byCollectionId(state, ownProps.collectionId)
  }
}

const mapDispatchToProps = {
  loadGeoJSON
}

export const CollectionMap = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableCollectionMap)
